<div class="tests-page">
  <section class="hero">
    <div class="hero-copy">
      <p class="eyebrow">Autopilot playground</p>
      <h1>Tests & experiments</h1>
      <p class="lede">
        A safe lane to stage ChatGPT/OpenAI ideas before they touch production.
        Iterate on invoices, comms, and data taps in one spot.
      </p>
      <div class="hero-actions">
        <button mat-flat-button color="primary" (click)="navigateTo('/tests/pdf-tests')">
          Open PDF lab
        </button>
        <button mat-stroked-button color="primary" (click)="getTransactions()" [disabled]="revenueState === 'loading'">
          {{ revenueState === 'loading' ? 'Pulling revenue...' : 'Pull revenue sample' }}
        </button>
        <button mat-stroked-button color="accent" (click)="validateAllInvoices()" [disabled]="validateInvoicesState === 'loading'">
          {{ validateInvoicesState === 'loading' ? 'Validating invoices...' : 'Validate all invoices' }}
        </button>
        <button mat-stroked-button color="accent" (click)="fetchInvoiceStatuses()" [disabled]="invoiceStatusState === 'loading'">
          {{ invoiceStatusState === 'loading' ? 'Fetching statuses...' : 'List invoice statuses' }}
        </button>
        <span class="pill muted" *ngIf="revenueState === 'idle'">Data tap idle</span>
        <span class="pill success" *ngIf="revenueState === 'done'">
          {{ revenueCount ?? 0 }} items @ {{ revenueLastRun | date:'shortTime' }}
        </span>
        <span class="pill error" *ngIf="revenueState === 'error'">{{ revenueError }}</span>
        <span class="pill success" *ngIf="validateInvoicesState === 'done'">
          {{ validateInvoicesResult?.updated ?? 0 }} updated / {{ validateInvoicesResult?.total ?? 0 }} checked
        </span>
        <span class="pill error" *ngIf="validateInvoicesState === 'error'">{{ validateInvoicesError }}</span>
        <span class="pill success" *ngIf="invoiceStatusState === 'done'">
          Statuses: {{ invoiceStatusResult | json }}
        </span>
        <span class="pill error" *ngIf="invoiceStatusState === 'error'">{{ invoiceStatusError }}</span>
      </div>
    </div>

    <div class="hero-panel">
      <div class="panel-title">
        <span>Next up: prompt -> invoice</span>
        <mat-icon>bolt</mat-icon>
      </div>
      <div class="panel-body">
        <div class="panel-row">
          <div>
            <p class="label">LLM brief</p>
            <p class="value">Single prompt to build invoice skeleton</p>
            <p class="hint">Map totals to template + email/SMS send</p>
          </div>
          <div class="badge">Designing</div>
        </div>
        <div class="panel-row small">
          <p class="label">Testing lanes</p>
          <div class="lanes">
            <span class="lane">PDF</span>
            <span class="lane">Email</span>
            <span class="lane">SMS</span>
            <span class="lane">Bank feeds</span>
          </div>
        </div>
        <div class="panel-row small">
          <p class="label">Notes</p>
          <p class="hint">Use this page to wire prototypes before flipping on OpenAI spend.</p>
        </div>
      </div>
    </div>
  </section>

  <section class="scratchpad">
    <div class="section-heading">
      <div>
        <p class="eyebrow">LLM prompt scratchpad</p>
        <h3>Describe the invoice you want to draft</h3>
        <p class="hint">Keep the details here; wire the call when you are ready to spend tokens.</p>
      </div>
      <button mat-stroked-button color="primary" (click)="recordPrompt()">Save idea</button>
    </div>
    <textarea [(ngModel)]="promptDraft" rows="3"></textarea>
    <div class="prompt-log" *ngIf="promptLog.length">
      <div class="prompt-chip" *ngFor="let prompt of promptLog">
        <div class="text">{{ prompt.text }}</div>
        <div class="time">{{ prompt.at | date:'shortTime' }}</div>
      </div>
    </div>
  </section>

  <section class="webflow">
    <div class="section-heading">
      <div>
        <p class="eyebrow">Webflow forms</p>
        <h3>Pull recent form submissions</h3>
        <p class="hint">Uses the configured Webflow token. Provide site/form ids to target a specific form or leave blank to auto-pick the first available.</p>
      </div>
      <div class="pill" [ngClass]="{ success: webflowState === 'done', error: webflowState === 'error' }">
        {{ webflowState === 'loading' ? 'Loading...' : webflowState === 'done' ? 'Ready' : webflowState === 'error' ? 'Error' : 'Idle' }}
      </div>
    </div>

    <div class="webflow-controls">
      <label>
        <span>Site ID (optional)</span>
        <input [(ngModel)]="webflowSiteId" placeholder="e.g. 1234abcd" />
      </label>
      <label>
        <span>Form ID (optional)</span>
        <input [(ngModel)]="webflowFormId" placeholder="e.g. 5678efgh" />
      </label>
      <label>
        <span>Limit</span>
        <input type="number" [(ngModel)]="webflowLimit" min="1" />
      </label>
      <label>
        <span>Offset</span>
        <input type="number" [(ngModel)]="webflowOffset" min="0" />
      </label>
      <button mat-flat-button color="primary" (click)="pullWebflowSubmissions()" [disabled]="webflowState === 'loading'">
        {{ webflowState === 'loading' ? 'Fetching...' : 'Fetch submissions' }}
      </button>
    </div>

    <div class="webflow-feedback">
      <span class="pill error" *ngIf="webflowError">{{ webflowError }}</span>
      <span class="pill success" *ngIf="webflowState === 'done' && !webflowError">
        {{ webflowSubmissions.length }} submissions • {{ webflowForms.length }} forms
        <ng-container *ngIf="webflowPagination"> • page {{ (webflowPagination.offset || 0) + 1 }} / {{ webflowPagination.total || '?' }}</ng-container>
      </span>
    </div>

    <div class="webflow-results" *ngIf="webflowState === 'done' && !webflowError">
      <div class="panel">
        <div class="panel-title">
          <span>Forms</span>
          <mat-icon>format_list_bulleted</mat-icon>
        </div>
        <div class="panel-body small" *ngIf="webflowForms.length; else noForms">
          <div class="panel-row" *ngFor="let form of webflowForms">
            <div>
              <p class="label">{{ form.displayName || form.formName || 'Untitled form' }}</p>
              <p class="hint">ID: {{ form.id || form._id }}</p>
            </div>
            <div class="pill muted">Method: {{ form.method || 'POST' }}</div>
          </div>
        </div>
        <ng-template #noForms>
          <p class="hint">Forms are not fetched by this endpoint. Provide a form ID above if you want to filter.</p>
        </ng-template>
      </div>

      <div class="panel">
        <div class="panel-title">
          <span>Submissions</span>
          <mat-icon>download_done</mat-icon>
        </div>
        <div class="panel-body small" *ngIf="webflowSubmissions.length; else noSubs">
          <div class="panel-row" *ngFor="let sub of webflowSubmissions">
            <div>
              <p class="label">Submitted {{ sub?.createdOn | date:'short' }}</p>
              <p class="hint">ID: {{ sub?.id || sub?._id }}</p>
            </div>
            <div class="fields">
              <div class="field" *ngFor="let entry of (sub?.data | keyvalue)">
                <span class="key">{{ entry.key }}:</span>
                <span class="value">{{ entry.value }}</span>
              </div>
              <div class="field" *ngIf="!sub?.data || !(sub?.data | keyvalue)?.length">
                <span class="key">Fields:</span>
                <span class="value">n/a</span>
              </div>
            </div>
          </div>
        </div>
        <ng-template #noSubs>
          <p class="hint">No submissions found for this form.</p>
        </ng-template>
      </div>
    </div>
  </section>

  <section class="card-section">
    <div class="section-heading">
      <div>
        <p class="eyebrow">Playground shortcuts</p>
        <h3>Jump into a test lane</h3>
      </div>
    </div>
    <div class="card-grid">
      <article class="card primary" (click)="openChatGPTInvoice()" [class.loading]="aiInvoiceLoading">
        <div class="icon primary">
          <mat-icon>description</mat-icon>
        </div>
        <div class="copy">
          <h4>Blank invoice - ChatGPT</h4>
          <p>Open a fresh standard invoice with ChatGPT pre-selected as the customer.</p>
          <div class="inline-actions">
            <span class="cta" *ngIf="!aiInvoiceLoading">Launch</span>
            <span class="pill muted" *ngIf="aiInvoiceLoading">Generating...</span>
            <button mat-stroked-button color="primary" class="inline-button" (click)="openBlankInvoiceDialog($event)">
              Open without AI
            </button>
          </div>
        </div>
      </article>
      <article class="card" *ngFor="let test of testShortcuts" (click)="navigateTo(test.route)">
        <div class="icon">
          <mat-icon>{{ test.icon }}</mat-icon>
        </div>
        <div class="copy">
          <h4>{{ test.title }}</h4>
          <p>{{ test.description }}</p>
          <span class="cta">Open</span>
        </div>
      </article>
      <article class="card coming-soon">
        <div class="icon">
          <mat-icon>auto_mode</mat-icon>
        </div>
        <div class="copy">
          <h4>Prompt -> invoice composer</h4>
          <p>Draft an invoice from a single prompt, choose template, and ship via email/SMS.</p>
          <span class="pill muted">Coming soon</span>
        </div>
      </article>
    </div>
    <div class="ai-error" *ngIf="aiInvoiceError">
      {{ aiInvoiceError }}
    </div>

    <div class="panel" *ngIf="validateInvoicesResult?.updatedInvoices?.length">
      <div class="panel-title">
        <span>Invoices updated on validation</span>
        <mat-icon>rule</mat-icon>
      </div>
      <div class="panel-body small">
        <div class="panel-row" *ngFor="let inv of validateInvoicesResult?.updatedInvoices">
          <div class="label">#{{ inv.number || inv.id }}</div>
          <div class="hint">matched: {{ inv.matched ?? 0 }}</div>
          <div class="pill muted">before: {{ inv.statusBefore || 'unset' }}</div>
          <div class="pill success">after: {{ inv.statusAfter || 'unset' }}</div>
        </div>
      </div>
    </div>
  </section>

  <section class="signals">
    <div class="section-heading">
      <div>
        <p class="eyebrow">Services & signals</p>
        <h3>What is wired for this playground</h3>
      </div>
    </div>
    <div class="signal-grid">
      <div class="signal" *ngFor="let signal of signals" [ngClass]="signal.tone">
        <div class="signal-top">
          <span class="label">{{ signal.label }}</span>
          <span class="pill" [ngClass]="signal.tone">{{ signal.status }}</span>
        </div>
        <p class="detail">{{ signal.detail }}</p>
      </div>
    </div>
  </section>
</div>
